import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { BORNES_PROFIL, type ChampDuProfil, type FeuilleDeProfil } from '@nextilt/commun';
import type { ContactDuProfil, LigneDeProfil, ReponseProfil } from '../partage/api/types';
import { IconeCalendrier, IconeDossier, IconeEclair, IconeFeuilleCornee, IconeSilhouette } from '../partage/icones';
import { useAuPouce, useCouche } from '../partage/surface';
import { ChoixDEnergie } from './ChoixDEnergie';
import {
  AJOUTER_UNE_INFO,
  A_REMPLIR,
  COMPLETER_MON_PROFIL,
  ECRIT_PAR_TOI,
  EXPLICATION_DU_SENSIBLE,
  FEUILLE_MES_CONTACTS,
  FEUILLE_PROPOSE_PAR_TON_IA,
  FEUILLE_SENSIBLE,
  GARDER_LA_PROPOSITION,
  INTITULE_DE_L_INFO,
  EXEMPLES_DES_CHAMPS_DU_PROFIL,
  MOTS_DES_CHAMPS_DU_PROFIL,
  ONGLET_MES_CONTACTS,
  ONGLET_MES_MOYENS,
  ONGLET_MOI,
  ONGLET_MON_RYTHME,
  ONGLET_PROPOSE_PAR_L_IA,
  ONGLET_SENSIBLE,
  ONGLET_TOUT,
  PAS_DE_CONTACT,
  PAS_DE_PROPOSITION,
  PROFIL,
  REFUSER_LA_PROPOSITION,
  RETIRER_L_INFO,
  SANS_INTITULE,
  SOUS_TITRE_DU_PROFIL,
  TITRES_DES_FEUILLES_DU_PROFIL,
  TOI,
  VALEUR_DE_L_INFO,
  corriger,
  mentionDeLaLigne,
  origineDeLaProposition,
  origineDuContact,
  propositionsATrier,
  texteDeLaProposition,
} from '../partage/vocabulaire';
import {
  CHAMPS_DE_LA_FEUILLE,
  ONGLETS_DU_PROFIL,
  champsVides,
  colonnesDeLOnglet,
  initialeDuProfil,
  nomDuContact,
  type FeuilleDeLaPage,
  type OngletDuProfil,
} from './reglesDuProfil';
import { FormulaireALaPremiereVisite, FormulaireDuProfil } from './FormulaireDuProfil';
import { useProfil } from './useProfil';
import { ProfilAuPouce } from './ProfilAuPouce';
import './Profil.css';

/**
 * La page Profil de l'ordinateur (chantier profil, ticket 02, maquette du carnet `profil.html`) :
 * ce que Nextilt sait de toi, que tu écris toi-même. En haut le rond de l'initiale, ton prénom et
 * la phrase ; dessous les onglets d'une boîte à fiches et, dans le classeur, les feuilles en
 * colonnes. L'onglet choisi est un état de la page, oublié en la quittant (comme Paramètres).
 *
 * Chaque champ et chaque ligne libre se corrige sur place : un clic ouvre la case, Entrée ou la
 * sortie de la case enregistre, Échap annule. Une seule case ouverte à la fois. Les contacts se
 * lisent seulement : ils se corrigent dans leur bulle.
 *
 * « Compléter mon profil » rouvre le premier formulaire (`FormulaireDuProfil`, ticket 03).
 *
 * « Proposé par ton IA » (chantier profil-pour-claude, ticket 02) : ce que Claude propose attend
 * là, à part, jusqu'à Garder (la ligne entre dans sa feuille, « proposé par Claude, gardé ») ou Non
 * (elle part) ; la pastille de l'onglet dit combien il en reste.
 *
 * Au téléphone, la page du téléphone (`ProfilAuPouce`, chantier telephone-profil, 01), couchée
 * en deux colonnes (02).
 */
export function Profil() {
  const auPouce = useAuPouce();
  // Couché, dans le cadre couché que toute page prend (`cadre/Cadre.tsx`, `useCouche`).
  const couche = useCouche();
  const profil = useProfil();
  const [onglet, setOnglet] = useState<OngletDuProfil>('tout');
  /** La case ouverte : `champ:<champ>`, `ligne:<id>:<partie>` ou `neuve:<feuille>`. */
  const [ouverte, setOuverte] = useState<string | null>(null);
  /** Le premier formulaire rouvert par « Compléter mon profil ». */
  const [formulaire, setFormulaire] = useState(false);
  if (auPouce || couche) return <ProfilAuPouce couche={couche && !auPouce} />;

  const lu = profil.lecture;
  const prenom = lu?.profil.prenom ?? null;
  const vides = lu ? champsVides(lu.profil) : [];

  return (
    <section className="profil">
      <header className="profil-haut">
        <span className="profil-avatar" aria-hidden="true">
          {initialeDuProfil(prenom)}
        </span>
        <div className="profil-qui">
          <h1>{prenom ?? TOI}</h1>
          <p>{SOUS_TITRE_DU_PROFIL}</p>
        </div>
        {/* Tant que des champs sont vides : il rouvre le premier formulaire, pré-rempli (ticket 03). */}
        {vides.length > 0 && (
          <button
            type="button"
            className="profil-completer"
            onClick={() => {
              setOuverte(null);
              setFormulaire(true);
            }}
          >
            {COMPLETER_MON_PROFIL}
          </button>
        )}
      </header>

      <div className="profil-onglets" role="tablist" aria-label={PROFIL}>
        {ONGLETS_DU_PROFIL.map(({ cle, ouvrable }) => (
          <button
            key={cle}
            type="button"
            role="tab"
            data-onglet={cle}
            aria-selected={cle === onglet}
            aria-disabled={ouvrable ? undefined : true}
            disabled={!ouvrable}
            className={`${cle === onglet ? 'est-choisi' : ''}${cle === 'sensible' ? ' profil-onglet--clos' : ''}`}
            onClick={() => {
              setOnglet(cle);
              setOuverte(null);
            }}
          >
            {MOTS_DES_ONGLETS[cle]}
            {/* La pastille de la maquette (`.pastille-n`) : le nombre de propositions à garder ou non. */}
            {cle === 'ia' && lu && lu.propositions.length > 0 && (
              <i className="profil-pastille" aria-label={propositionsATrier(lu.propositions.length)}>
                {lu.propositions.length}
              </i>
            )}
          </button>
        ))}
      </div>

      <div className="profil-classeur" role="tabpanel" aria-label={MOTS_DES_ONGLETS[onglet]}>
        {profil.annonce && (
          <p className="profil-annonce" role="status">
            {profil.annonce}
          </p>
        )}
        {lu && (
          <div className="profil-colonnes">
            {colonnesDeLOnglet(onglet).map((colonne) => (
              <div key={colonne.join()} className="profil-colonne">
                {colonne.map((feuille) => (
                  <FeuilleDuProfil
                    key={feuille}
                    feuille={feuille}
                    lu={lu}
                    profil={profil}
                    ouverte={ouverte}
                    ouvrir={setOuverte}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/*
       * Rouvert à la main, ou ouvert seul pour un compte qui ne l'a pas encore vu et arrive ici
       * sans être passé par l'accueil (décision 5 d'Igor : à la première ouverture de Profil).
       */}
      {formulaire ? <FormulaireDuProfil onFermer={() => setFormulaire(false)} /> : <FormulaireALaPremiereVisite />}
    </section>
  );
}

const MOTS_DES_ONGLETS: Record<OngletDuProfil, string> = {
  tout: ONGLET_TOUT,
  moi: ONGLET_MOI,
  moyens: ONGLET_MES_MOYENS,
  rythme: ONGLET_MON_RYTHME,
  contacts: ONGLET_MES_CONTACTS,
  ia: ONGLET_PROPOSE_PAR_L_IA,
  sensible: ONGLET_SENSIBLE,
};

const ICONE = 'profil-icone';
const ICONES_DES_FEUILLES: Record<FeuilleDeProfil, ReactNode> = {
  'qui-je-suis': <IconeSilhouette className={ICONE} />,
  'ce-que-j-ai': <IconeDossier className={ICONE} />,
  'ma-facon-de-faire': <IconeCalendrier className={ICONE} />,
};

type Gestes = ReturnType<typeof useProfil>;

/** Une feuille du classeur, selon sa sorte. */
function FeuilleDuProfil({
  feuille,
  lu,
  profil,
  ouverte,
  ouvrir,
}: {
  feuille: FeuilleDeLaPage;
  lu: ReponseProfil;
  profil: Gestes;
  ouverte: string | null;
  ouvrir: (cle: string | null) => void;
}) {
  if (feuille === 'sensible') {
    return (
      <section className="profil-feuille profil-feuille--verrou" data-feuille="sensible">
        <h2>
          <IconeCadenas />
          {FEUILLE_SENSIBLE}
        </h2>
        <p>{EXPLICATION_DU_SENSIBLE}</p>
      </section>
    );
  }
  if (feuille === 'propose') {
    return (
      <section className="profil-feuille profil-feuille--propose" data-feuille="propose">
        <h2>
          <IconeEclair className={ICONE} />
          {FEUILLE_PROPOSE_PAR_TON_IA}
        </h2>
        {lu.propositions.length === 0 ? (
          <p>{PAS_DE_PROPOSITION}</p>
        ) : (
          lu.propositions.map((p) => (
            <Proposition
              key={p.id}
              proposition={p}
              onGarder={() => profil.garderUneProposition(p)}
              onRefuser={() => profil.refuserUneProposition(p)}
            />
          ))
        )}
      </section>
    );
  }
  if (feuille === 'contacts') {
    return (
      <section className="profil-feuille" data-feuille="contacts">
        <h2>
          <IconeFeuilleCornee className={ICONE} />
          {FEUILLE_MES_CONTACTS}
        </h2>
        {lu.contacts.length === 0 ? (
          <p className="profil-vide">{PAS_DE_CONTACT}</p>
        ) : (
          lu.contacts.map((c) => <LigneDeContact key={`${c.bulle.id}:${c.telephone ?? ''}`} contact={c} />)
        )}
      </section>
    );
  }

  const fermer = () => ouvrir(null);
  return (
    <section className="profil-feuille" data-feuille={feuille}>
      <h2>
        {ICONES_DES_FEUILLES[feuille]}
        {TITRES_DES_FEUILLES_DU_PROFIL[feuille]}
      </h2>
      {CHAMPS_DE_LA_FEUILLE[feuille].map((champ) => (
        <LigneDeChamp
          key={champ}
          champ={champ}
          valeur={lu.profil[champ]}
          ouverte={ouverte === `champ:${champ}`}
          onOuvrir={() => ouvrir(`champ:${champ}`)}
          onFermer={fermer}
          onEnregistrer={(ecrit) => profil.corrigerUnChamp(champ, ecrit)}
        />
      ))}
      {lu.lignes[feuille].map((ligne) => (
        <LigneLibre
          key={ligne.id}
          ligne={ligne}
          ouverte={ouverte?.startsWith(`ligne:${ligne.id}:`) ? (ouverte.endsWith(':intitule') ? 'intitule' : 'valeur') : null}
          onOuvrir={(partie) => ouvrir(`ligne:${ligne.id}:${partie}`)}
          onFermer={fermer}
          onCorriger={(partie, ecrit) => profil.corrigerUneLigne(ligne, partie, ecrit)}
          onRetirer={() => profil.retirerUneLigne(ligne)}
        />
      ))}
      {ouverte === `neuve:${feuille}` ? (
        <LigneNeuve
          onFermer={fermer}
          onEnregistrer={(intitule, valeur) => void profil.ajouterUneLigne(feuille, intitule, valeur)}
        />
      ) : (
        <button type="button" className="profil-ajouter" onClick={() => ouvrir(`neuve:${feuille}`)}>
          {AJOUTER_UNE_INFO}
        </button>
      )}
    </section>
  );
}

/** Un champ du profil : son nom à gauche, sa valeur à droite, « écrit par toi » dessous. */
function LigneDeChamp({
  champ,
  valeur,
  ouverte,
  onOuvrir,
  onFermer,
  onEnregistrer,
}: {
  champ: ChampDuProfil;
  valeur: string | null;
  ouverte: boolean;
  onOuvrir: () => void;
  onFermer: () => void;
  onEnregistrer: (ecrit: string) => void;
}) {
  const nom = MOTS_DES_CHAMPS_DU_PROFIL[champ];
  // L'énergie se choisit en trois boutons, toujours visibles (retour d'Igor du 2026-10-21).
  if (champ === 'courage') {
    return (
      <div className="profil-info" data-champ={champ}>
        <span>{nom}</span>
        <ChoixDEnergie nom={nom} valeur={valeur} onChoisir={onEnregistrer} />
      </div>
    );
  }
  return (
    <div className="profil-info" data-champ={champ}>
      <span>{nom}</span>
      <CaseACorriger
        nom={nom}
        valeur={valeur ?? ''}
        vide={A_REMPLIR}
        exemple={EXEMPLES_DES_CHAMPS_DU_PROFIL[champ]}
        mention={valeur !== null ? ECRIT_PAR_TOI : null}
        ouverte={ouverte}
        onOuvrir={onOuvrir}
        onFermer={onFermer}
        onEnregistrer={onEnregistrer}
      />
    </div>
  );
}

/** Une ligne libre : son intitulé et sa valeur se corrigent chacun sur place ; × la retire. */
function LigneLibre({
  ligne,
  ouverte,
  onOuvrir,
  onFermer,
  onCorriger,
  onRetirer,
}: {
  ligne: LigneDeProfil;
  ouverte: 'intitule' | 'valeur' | null;
  onOuvrir: (partie: 'intitule' | 'valeur') => void;
  onFermer: () => void;
  onCorriger: (partie: 'intitule' | 'valeur', ecrit: string) => void;
  onRetirer: () => void;
}) {
  return (
    <div className="profil-info profil-info--libre" data-ligne={ligne.id}>
      <CaseACorriger
        nom={INTITULE_DE_L_INFO}
        valeur={ligne.intitule}
        vide={SANS_INTITULE}
        mention={null}
        intitule
        ouverte={ouverte === 'intitule'}
        onOuvrir={() => onOuvrir('intitule')}
        onFermer={onFermer}
        onEnregistrer={(ecrit) => onCorriger('intitule', ecrit)}
      />
      <CaseACorriger
        nom={ligne.intitule || VALEUR_DE_L_INFO}
        valeur={ligne.valeur}
        vide={A_REMPLIR}
        mention={mentionDeLaLigne(ligne)}
        ouverte={ouverte === 'valeur'}
        onOuvrir={() => onOuvrir('valeur')}
        onFermer={onFermer}
        onEnregistrer={(ecrit) => onCorriger('valeur', ecrit)}
      />
      <button type="button" className="profil-retirer" aria-label={RETIRER_L_INFO} title={RETIRER_L_INFO} onClick={onRetirer}>
        ×
      </button>
    </div>
  );
}

/**
 * Une valeur qui se corrige sur place : un clic ouvre la case, Entrée ou la sortie de la case
 * enregistre, Échap annule (il s'arrête à la case : rien d'autre ne se ferme, comme une ligne de
 * liste libre).
 */
function CaseACorriger({
  nom,
  valeur,
  vide,
  exemple = '',
  mention,
  intitule = false,
  ouverte,
  onOuvrir,
  onFermer,
  onEnregistrer,
}: {
  nom: string;
  valeur: string;
  vide: string;
  /** Un exemple grisé dans la case ouverte et vide. */
  exemple?: string;
  mention: string | null;
  /** L'intitulé d'une ligne libre, à gauche, et non une valeur à droite. */
  intitule?: boolean;
  ouverte: boolean;
  onOuvrir: () => void;
  onFermer: () => void;
  onEnregistrer: (ecrit: string) => void;
}) {
  const classe = intitule ? 'profil-case profil-case--intitule' : 'profil-case';
  if (!ouverte) {
    return (
      <button type="button" className={classe} aria-label={corriger(nom)} onClick={onOuvrir}>
        {valeur === '' ? <span className="profil-a-remplir">{vide}</span> : valeur}
        {mention && <em>{mention}</em>}
      </button>
    );
  }

  return (
    <CaseOuverte classe={classe} nom={nom} valeur={valeur} exemple={exemple} onFermer={onFermer} onEnregistrer={onEnregistrer} />
  );
}

/** La case ouverte : elle part de la valeur lue à son ouverture. */
function CaseOuverte({
  classe,
  nom,
  valeur,
  exemple,
  onFermer,
  onEnregistrer,
}: {
  classe: string;
  nom: string;
  valeur: string;
  exemple: string;
  onFermer: () => void;
  onEnregistrer: (ecrit: string) => void;
}) {
  const [ecrit, setEcrit] = useState(valeur);
  const fini = useRef(false);
  const terminer = (garder: boolean) => {
    if (fini.current) return;
    fini.current = true;
    if (garder) onEnregistrer(ecrit);
    onFermer();
  };
  return (
    <input
      className={`${classe} profil-case--ouverte`}
      value={ecrit}
      placeholder={exemple}
      maxLength={BORNES_PROFIL.texteMax}
      aria-label={nom}
      autoFocus
      onChange={(e) => setEcrit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          terminer(true);
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          terminer(false);
        }
      }}
      onBlur={() => terminer(true)}
    />
  );
}

/**
 * « + ajouter une info » ouvert : un intitulé et une valeur. Entrée, ou la sortie des deux cases,
 * l'ajoute s'il y a quelque chose d'écrit ; Échap annule.
 */
function LigneNeuve({
  onFermer,
  onEnregistrer,
}: {
  onFermer: () => void;
  onEnregistrer: (intitule: string, valeur: string) => void;
}) {
  const [intitule, setIntitule] = useState('');
  const [valeur, setValeur] = useState('');
  const fini = useRef(false);
  const terminer = (garder: boolean) => {
    if (fini.current) return;
    fini.current = true;
    if (garder) onEnregistrer(intitule, valeur);
    onFermer();
  };
  const touches = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      terminer(true);
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      terminer(false);
    }
  };
  return (
    <div
      className="profil-info profil-info--neuve"
      onBlur={(e) => {
        // On passe de l'intitulé à la valeur : la ligne reste ouverte.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) terminer(true);
      }}
    >
      <input
        className="profil-case profil-case--intitule profil-case--ouverte"
        value={intitule}
        maxLength={BORNES_PROFIL.texteMax}
        placeholder={INTITULE_DE_L_INFO}
        aria-label={INTITULE_DE_L_INFO}
        autoFocus
        onChange={(e) => setIntitule(e.target.value)}
        onKeyDown={touches}
      />
      <input
        className="profil-case profil-case--ouverte"
        value={valeur}
        maxLength={BORNES_PROFIL.texteMax}
        placeholder={VALEUR_DE_L_INFO}
        aria-label={VALEUR_DE_L_INFO}
        onChange={(e) => setValeur(e.target.value)}
        onKeyDown={touches}
      />
    </div>
  );
}

/**
 * Une proposition de Claude (chantier profil-pour-claude, ticket 02, maquette `.prop`) : ce
 * qu'elle propose entre guillemets, d'où elle vient en petit, Garder et Non.
 */
function Proposition({
  proposition,
  onGarder,
  onRefuser,
}: {
  proposition: LigneDeProfil;
  onGarder: () => void;
  onRefuser: () => void;
}) {
  return (
    <div className="profil-proposition" data-proposition={proposition.id}>
      <span className="profil-proposition-texte">{texteDeLaProposition(proposition)}</span>
      <small>{origineDeLaProposition(proposition)}</small>
      <button type="button" className="profil-proposition-geste" onClick={onGarder}>
        {GARDER_LA_PROPOSITION}
      </button>
      <button type="button" className="profil-proposition-geste profil-proposition-geste--doux" onClick={onRefuser}>
        {REFUSER_LA_PROPOSITION}
      </button>
    </div>
  );
}

/** Un contact relu dans une bulle : son nom, son téléphone et son e-mail, sa bulle et son projet. */
function LigneDeContact({ contact }: { contact: ContactDuProfil }) {
  return (
    <div className="profil-info" data-contact={contact.bulle.id}>
      <span>{nomDuContact(contact)}</span>
      <b>
        {contact.telephone && <span className="profil-coordonnee">{contact.telephone}</span>}
        {contact.email && <span className="profil-coordonnee">{contact.email}</span>}
        <em>{origineDuContact(contact)}</em>
      </b>
    </div>
  );
}

/** Le cadenas de la feuille Sensible, dessiné comme celui de la maquette. */
function IconeCadenas() {
  return (
    <svg className="profil-icone profil-icone--cadenas" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
